import React, { useEffect, useState } from 'react';
import {
    Alert,
    Button,
    Group,
    Modal,
    NumberInput,
    Select,
    Stack,
    Text,
    TextInput,
} from '@mantine/core';
import { Info, Warning } from '@phosphor-icons/react';

import { setReceiptCost } from '@api/vendor-portal';
import { formatMaterialQuantity } from '@utils/materialQuantity';
import { readApiError } from '@utils/apiError';
import { formatCount } from '@utils/currency';

/** The receipt being costed. */
export interface CostableReceipt {
    id: string;
    materialName: string;
    materialId: string;
    unit?: string;
    quantity: number;
    date: string;
    vendorName?: string;
}

interface ReceiptCostModalProps {
    receipt: CostableReceipt | null;
    onClose: () => void;
    onSaved: () => void;
}

/**
 * Suggested currencies. Not a whitelist - the backend accepts any three-letter code, and
 * every brand on the platform buys in rupees today.
 */
const CURRENCIES = ['INR', 'USD', 'CNY', 'EUR', 'GBP'];

/**
 * Records what a brand paid for a receipt its manufacturer already posted.
 *
 * The rate is what gets stored, not the line total: a short shipment leaves the rate true
 * while invalidating a total, and the rate is what you compare across deliveries. The total
 * is shown alongside purely so a mistyped rate is visible before it is saved.
 */
const ReceiptCostModal: React.FC<ReceiptCostModalProps> = ({
    receipt,
    onClose,
    onSaved,
}) => {
    const [unitCost, setUnitCost] = useState<string | number>('');
    const [currency, setCurrency] = useState<string>('INR');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (receipt) {
            setUnitCost('');
            setCurrency('INR');
            setError(null);
            setSaving(false);
        }
    }, [receipt]);

    if (!receipt) return null;

    const rate = Number(unitCost);
    const rateIsUsable = unitCost !== '' && Number.isFinite(rate) && rate >= 0;
    const lineTotal = rateIsUsable ? rate * receipt.quantity : null;
    const unitLabel = receipt.unit ?? 'unit';

    const handleSave = async () => {
        if (!rateIsUsable) {
            setError('Enter the rate paid per unit.');
            return;
        }

        setSaving(true);
        setError(null);
        try {
            await setReceiptCost({ eventId: receipt.id, unitCost: rate, currency });
            onSaved();
            onClose();
        } catch (err) {
            setError(readApiError(err, 'Could not save the cost. Please try again.'));
            setSaving(false);
        }
    };

    return (
        <Modal opened onClose={onClose} title="Record cost" size="md" centered>
            <Stack gap="md">
                <Stack gap={2}>
                    <Text size="sm" fw={600}>
                        {receipt.materialName}
                    </Text>
                    <Text size="xs" c="dimmed">
                        {formatMaterialQuantity(receipt.quantity, receipt.unit)} received
                        {receipt.vendorName ? ` from ${receipt.vendorName}` : ''} on{' '}
                        {receipt.date}
                    </Text>
                </Stack>

                <Group grow align="flex-start">
                    <NumberInput
                        label={`Rate (excl. GST) per ${unitLabel}`}
                        description="What you paid, before tax"
                        placeholder="0.00"
                        value={unitCost}
                        onChange={setUnitCost}
                        min={0}
                        decimalScale={6}
                        data-autofocus
                    />
                    <Select
                        label="Currency"
                        data={CURRENCIES}
                        value={currency}
                        onChange={(v) => setCurrency(v ?? 'INR')}
                        allowDeselect={false}
                    />
                </Group>

                <TextInput
                    label="Line total"
                    description="Calculated — not saved"
                    value={
                        lineTotal === null
                            ? '—'
                            : `${currency} ${formatCount(lineTotal, { decimals: 2 })}`
                    }
                    readOnly
                />

                <Alert color="blue" icon={<Info size={16} />}>
                    <Text size="xs">
                        Enter the rate <strong>excluding GST</strong> — recoverable tax is
                        reclaimed, so it is not part of what the material cost you. The
                        manufacturer never sees this figure.
                    </Text>
                </Alert>

                {error && (
                    <Alert color="red" icon={<Warning size={16} />}>
                        {error}
                    </Alert>
                )}

                <Group justify="flex-end">
                    <Button variant="subtle" color="gray" onClick={onClose}>
                        Cancel
                    </Button>
                    <Button onClick={handleSave} loading={saving} disabled={!rateIsUsable}>
                        Save cost
                    </Button>
                </Group>
            </Stack>
        </Modal>
    );
};

export default ReceiptCostModal;
